import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../../../services/api';
import {
  AnalysisRange,
  AnalysisResult,
  AVGTAnalysis,
  TrendChartPoint,
} from '../types/health.types';
import { HealthSectionKey } from '../config/healthAnalysisConfig';
import { windowHealth } from '../utils/healthStatus';

// STD-015: server state for the health analysis domain. apiService is imported
// here (a *.queries.ts file) per STD-016. The original service caught all
// errors and returned empty arrays (console.error-only), so all queries opt
// out of the global error toast.

export const healthAnalysisKeys = {
  all: ['healthAnalysis'] as const,
  byDroneAndSection: (serial: string, section: string) =>
    [...healthAnalysisKeys.all, serial, section] as const,
};

// --- Pure (non-fetch) transform helpers moved from HealthAnalysisService ---

// Title, range and bad direction come from the backend, which reads them from
// the one health catalogue (packages/fleet_tools/health_catalogue.py, #1207).
// `range` is null where no range has been validated; the status is then
// `unknown`, never a WARNING the panel cannot justify.
export type BackendAnalysis = {
  parameter: string;
  trend_value: number | null;
  confidence: number;
  forecast_value: number | null;
  forecast_date: string | null;
  chart_data?: TrendChartPoint[];
  latest_value?: number | null;
  latest_date?: string | null;
  unit?: string;
  title?: string;
  range?: AnalysisRange | null;
  bad_direction?: 'up' | 'down' | 'either' | 'none';
  on_panel?: boolean;
};

export function buildAVGTAnalysis(backendAnalysis: BackendAnalysis): AVGTAnalysis | null {
  if (
    backendAnalysis.parameter === 'insufficient_data' ||
    backendAnalysis.parameter === 'no_data' ||
    backendAnalysis.on_panel === false
  ) {
    return null;
  }

  // Status covers the whole plotted window, not just the newest point (#1269).
  const health = windowHealth(backendAnalysis.chart_data, backendAnalysis.range ?? undefined);

  return {
    type: 'AVGT',
    title: backendAnalysis.title ?? backendAnalysis.parameter,
    parameter: backendAnalysis.parameter,
    trendValue: backendAnalysis.trend_value,
    unit: backendAnalysis.unit ?? '',
    forecastValue: backendAnalysis.forecast_value,
    forecastDate: backendAnalysis.forecast_date ?? undefined,
    badDirection: backendAnalysis.bad_direction,
    confidence: backendAnalysis.confidence,
    chartData: backendAnalysis.chart_data ?? [],
    latestValue: backendAnalysis.latest_value,
    status: health.status,
    outOfBandCount: health.outOfBandCount,
    windowSize: health.windowSize,
    latestDate: backendAnalysis.latest_date ?? undefined,
  };
}

// --- Query hooks ---

export function useHealthAnalysisQuery(
  droneSerial: string | null,
  sectionKey: HealthSectionKey
): { analyses: AnalysisResult[]; isLoading: boolean; error: string | null } {
  const query = useQuery({
    queryKey: healthAnalysisKeys.byDroneAndSection(droneSerial ?? '', sectionKey),
    enabled: !!droneSerial,
    meta: { skipGlobalErrorToast: true },
    queryFn: async (): Promise<AnalysisResult[]> => {
      const response = await apiService.getHealthTrendAnalysis(droneSerial!, sectionKey);
      const analyses: AnalysisResult[] = [];
      for (const backendAnalysis of response.analyses) {
        const result = buildAVGTAnalysis(backendAnalysis);
        if (result) analyses.push(result);
      }
      return analyses;
    },
  });

  return {
    analyses: query.data ?? [],
    isLoading: query.isLoading,
    error: query.isError ? 'Failed to load health analysis data' : null,
  };
}
